import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useApiErrorMessage } from '@/shared/i18n/useApiErrorMessage';
import { Button, Field, Skeleton, useToastStore } from '@/shared/ui';

import { useElement, useUpdateElement } from '../queries/useElement';
import { ElementHistory } from './ElementHistory';
import { OwnersSection } from './OwnersSection';
import type { ElementDetail } from '../model/types';
import styles from './ElementScreen.module.css';
import { RelationSections } from './RelationSections';

/**
 * Ekran elementu — główne miejsce pracy (spec sek. 4): tytuł, typ, opis,
 * właściciele z proweniencją, relacje pogrupowane, licznik zadań.
 *
 * ⚠ Drzewko tu NIE mieszka. Podział ról jest rozstrzygnięty decyzją 63
 * (*Constellation = „co się dzieje wokół elementu", listy = „co jest na mnie"*)
 * i decyzją 70 — drzewko ma własną trasę i własne zapytanie.
 */
export function ElementScreen() {
  const { id } = useParams<{ id: string }>();
  const elementId = Number(id);

  if (!Number.isInteger(elementId) || elementId <= 0) {
    // Adres jest częścią produktu i bywa wklejany ręcznie (spec 4A), więc
    // „/element/abc" to normalny przypadek, nie awaria warta wyjątku.
    return <InvalidId />;
  }

  return <ElementScreenContent elementId={elementId} />;
}

function InvalidId() {
  const { t } = useTranslation();

  return (
    <div className={styles['failure']} role="alert">
      <p>{t('graph.element.invalidId')}</p>
    </div>
  );
}

function ElementScreenContent({ elementId }: { elementId: number }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const query = useElement(elementId);

  if (query.isPending) {
    return (
      <div className={styles['loading']} aria-busy="true">
        <Skeleton width="18rem" height="2rem" announce />
        <Skeleton height="6rem" />
        <Skeleton height="10rem" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className={styles['failure']} role="alert">
        <p>{describeError(query.error)}</p>
        <Button onClick={() => void query.refetch()}>{t('common.action.retry')}</Button>
      </div>
    );
  }

  return <ElementView element={query.data} />;
}

function ElementView({ element }: { element: ElementDetail }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);

  return (
    <article className={styles['screen']}>
      <header className={styles['header']}>
        <div className={styles['headline']}>
          <h1 className={styles['title']} title={element.title}>
            {element.title}
          </h1>

          <div className={styles['meta']}>
            {element.type ? <span className={styles['badge']}>{element.type}</span> : null}

            {/* Licznik zadań NA TYM elemencie, wszystkich userów, bez agregacji
                poddrzewa (decyzja 35). Same zadania są w detalu, nie na drzewku. */}
            {element.activeTaskCount > 0 ? (
              <span className={`${styles['badge']} ${styles['badgeWork']}`}>
                {t('common.count.commitment', { count: element.activeTaskCount })}
              </span>
            ) : null}
          </div>
        </div>

        <div className={styles['actions']}>
          <CopyLinkButton />

          {/* Uprawnienia liczy serwer (spec 9.2: własność bywa dziedziczona,
              więc front nie ma z czego ich wyprowadzić). To ukrycie akcji,
              nie kontrola dostępu — tej broni API. */}
          {element.canEdit && !editing ? (
            <Button variant="primary" onClick={() => setEditing(true)}>
              {t('graph.element.edit')}
            </Button>
          ) : null}
        </div>
      </header>

      {editing ? (
        <EditForm element={element} onDone={() => setEditing(false)} />
      ) : (
        <section className={styles['section']} aria-labelledby="element-description">
          <h2 id="element-description" className={styles['sectionTitle']}>
            {t('graph.element.description')}
          </h2>

          {element.description ? (
            <p className={styles['description']}>{element.description}</p>
          ) : (
            <p className={styles['empty']}>{t('graph.element.noDescription')}</p>
          )}
        </section>
      )}

      <OwnersSection element={element} />

      <section className={styles['section']} aria-labelledby="element-relations">
        <h2 id="element-relations" className={styles['sectionTitle']}>
          {t('graph.element.relations')}
        </h2>

        <RelationSections relations={element.relations} />
      </section>

      {/* ⚠ Historia jest ZWINIĘTA na starcie i ma OSOBNE wywołanie (decyzja 104):
          większość wejść na element dotyczy stanu „jak jest", a nie „jak było",
          więc pobieranie jej zawsze byłoby podatkiem od każdego otwarcia. */}
      <section className={styles['section']} aria-labelledby="element-history">
        <h2 id="element-history" className={styles['sectionTitle']}>
          {t('graph.history.title')}
        </h2>

        <ElementHistory elementId={element.id} />
      </section>
    </article>
  );
}

function CopyLinkButton() {
  const { t } = useTranslation();
  const show = useToastStore((state) => state.show);

  async function copy() {
    try {
      await navigator.clipboard.writeText(globalThis.location.href);
      show({ message: t('graph.element.linkCopied'), tone: 'success' });
    } catch {
      // Schowek bywa niedostępny (brak zgody, kontekst bez HTTPS). Cisza byłaby
      // gorsza niż komunikat — człowiek kliknął i musi wiedzieć, że nic nie ma.
      show({ message: t('graph.element.linkCopyFailed'), tone: 'danger' });
    }
  }

  return <Button onClick={() => void copy()}>{t('graph.element.copyLink')}</Button>;
}

function EditForm({ element, onDone }: { element: ElementDetail; onDone: () => void }) {
  const { t } = useTranslation();
  const describeError = useApiErrorMessage();
  const show = useToastStore((state) => state.show);
  const update = useUpdateElement(element.id);

  const [title, setTitle] = useState(element.title);
  const [type, setType] = useState(element.type ?? '');
  const [description, setDescription] = useState(element.description ?? '');
  const [touched, setTouched] = useState(false);

  const titleError = touched && title.trim() === '' ? t('graph.element.titleRequired') : undefined;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setTouched(true);

    if (title.trim() === '') {
      return;
    }

    const before = { title: element.title, type: element.type, description: element.description };

    update.mutate(
      { title: title.trim(), type: type.trim() || null, description: description.trim() || null },
      {
        onSuccess: () => {
          onDone();

          // Spec 4B: „Cofnij" w toaście zamiast blokującego potwierdzenia.
          // Cofnięcie to zwykła druga mutacja — stan sprzed zmiany mamy,
          // bo formularz startował z niego.
          show({
            message: t('graph.element.saved'),
            action: {
              label: t('common.action.undo'),
              onAction: () => update.mutate(before),
            },
          });
        },
      },
    );
  }

  return (
    <section className={styles['section']}>
      <form className={styles['form']} onSubmit={onSubmit} noValidate>
        <Field
          label={t('graph.element.title')}
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          {...(titleError ? { error: titleError } : {})}
          /*
           * Formularz pojawia się WYŁĄCZNIE po kliknięciu „Edytuj", więc przeniesienie
           * ogniska jest spełnieniem intencji, a nie porwaniem jej. Reguła broni przed
           * autofokusem przy wejściu na stronę — tego przypadku tu nie ma, a bez tego
           * użytkownik klawiatury musi wracać tabem do pola, o które sam poprosił.
           */
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />

        <Field
          label={t('graph.element.type')}
          value={type}
          onChange={(event) => setType(event.target.value)}
          hint={t('graph.element.typeHint')}
        />

        <label className={styles['textareaLabel']}>
          {t('graph.element.description')}
          <textarea
            className={styles['textarea']}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>

        {update.isError ? (
          <p className={styles['empty']} role="alert">
            {describeError(update.error)}
          </p>
        ) : null}

        <div className={styles['formActions']}>
          <Button type="submit" variant="primary" disabled={update.isPending}>
            {t('common.action.save')}
          </Button>

          <Button variant="ghost" onClick={onDone} disabled={update.isPending}>
            {t('common.action.cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
}
